import type { ReactNode } from "react";
import { Button as RACButton } from "react-aria-components";
import { hueFromName, hueVars, type HueOrNeutral } from "../hue";
import { Icon, IconX } from "../icon";
import { useStrings } from "../strings";
import { cx } from "../tv";

/** Tag pill: subtle tint, hue text, 1 px hue border, never a solid fill (DESIGN-RULES 8). The hue comes from the
 *  name unless given, so the same tag looks the same everywhere. */
export function Tag({ children, hue, dot, className }: { children: string; hue?: HueOrNeutral; dot?: boolean; className?: string }) {
  return (
    <span
      style={hueVars(hue ?? hueFromName(children))}
      className={cx(
        "inline-flex h-[21px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill bg-hue-subtle px-2 text-xs font-semibold text-hue-text",
        "shadow-[inset_0_0_0_1px_var(--hue-border)]",
        className,
      )}
    >
      {dot && <i aria-hidden className="size-1.5 rounded-[2px] bg-hue" />}
      {children}
    </span>
  );
}

/** At most two tags on a row, then "+n" with the rest in a tooltip-able title. */
export function TagList({ tags, max = 2, className }: { tags: { label: string; hue?: HueOrNeutral }[]; max?: number; className?: string }) {
  const extra = tags.length - max;
  return (
    <span className={cx("inline-flex min-w-0 items-center gap-1 overflow-hidden", className)}>
      {tags.slice(0, max).map((t) => <Tag key={t.label} hue={t.hue}>{t.label}</Tag>)}
      {extra > 0 && <span className="tabular text-xs font-medium text-fg-subtle" title={tags.slice(max).map((t) => t.label).join(", ")}>+{extra}</span>}
    </span>
  );
}

/** Small count chip; tinted by the surrounding hue when there is one. */
export function CountChip({ children, tone = "neutral", className }: { children: ReactNode; tone?: "neutral" | "hue" | "accent"; className?: string }) {
  return (
    <span
      className={cx(
        "tabular inline-grid h-5 min-w-[22px] place-items-center rounded-pill px-1.5 text-xs font-semibold",
        tone === "neutral" && "bg-surface-2 text-fg-muted",
        tone === "hue" && "bg-[color-mix(in_oklch,var(--hue-solid)_16%,transparent)] text-hue-text",
        tone === "accent" && "bg-accent text-accent-fg",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Filter chip: quiet outline, filled when on; removable when it carries a value. */
export function Chip({ children, isOn, onPress, onRemove, icon }: { children: ReactNode; isOn?: boolean; onPress?: () => void; onRemove?: () => void; icon?: ReactNode }) {
  const t = useStrings();
  return (
    <span className={cx("inline-flex h-7 shrink-0 items-center rounded-pill text-sm font-medium", isOn ? "bg-sel text-fg" : "text-fg-muted shadow-[inset_0_0_0_1px_var(--row-line)]")}>
      <RACButton onPress={onPress} className="flex h-full items-center gap-1.5 whitespace-nowrap rounded-pill ps-2.5 pe-2.5 outline-none hovered:text-fg focus-visible:shadow-focus">
        {icon}
        {children}
      </RACButton>
      {onRemove && (
        <RACButton aria-label={t.clear} onPress={onRemove} className="-ms-1 me-1 grid size-5 place-items-center rounded-pill text-fg-subtle outline-none hovered:bg-hover focus-visible:shadow-focus">
          <Icon icon={IconX} size="chip" className="size-3" />
        </RACButton>
      )}
    </span>
  );
}
